import { useState, useCallback, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  loadCustomTableOrder,
  saveCustomTableOrder,
  clearCustomTableOrder,
  sortByCustomOrder,
  deriveDbTableOrder,
} from "@/lib/tableOrderUtils";
import { tablesQueryKey } from "@/hooks/queries/useTablesQuery";
import { combinedTablesQueryKey } from "@/hooks/queries/useCombinedTablesQuery";

interface TableRow {
  id: string;
  name: string;
  type: "table" | "combined";
}

interface OrderableEntity {
  id: string;
  displayOrder?: number | null;
}

const naturalSort = (a: TableRow, b: TableRow) =>
  a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });

async function persistOrderToDb(
  establishmentId: string,
  rows: TableRow[]
): Promise<void> {
  const payload = rows.map((r, position) => ({
    id: r.id,
    type: r.type, // 'table' | 'combined'
    position,
  }));
  const { error } = await supabase.rpc("update_table_display_order", {
    _establishment_id: establishmentId,
    _order: payload,
  });
  if (error) throw error;
}

async function clearOrderInDb(establishmentId: string): Promise<void> {
  const [a, b] = await Promise.all([
    supabase
      .from("tables")
      .update({ display_order: null })
      .eq("establishment_id", establishmentId),
    supabase
      .from("combined_tables")
      .update({ display_order: null })
      .eq("establishment_id", establishmentId),
  ]);
  if (a.error) throw a.error;
  if (b.error) throw b.error;
}

export function useTimelineTableOrder(
  defaultRows: TableRow[],
  establishmentId: string | undefined,
  tables?: OrderableEntity[],
  combinedTables?: OrderableEntity[]
) {
  const queryClient = useQueryClient();

  // Detect DB order from passed tables/combined (priority over localStorage)
  const dbOrder = useMemo(
    () => deriveDbTableOrder([...(tables ?? []), ...(combinedTables ?? [])]),
    [tables, combinedTables]
  );

  const [localOrder, setLocalOrder] = useState<string[] | null>(() =>
    loadCustomTableOrder(establishmentId)
  );

  useEffect(() => {
    setLocalOrder(loadCustomTableOrder(establishmentId));
  }, [establishmentId]);

  // Effective order: DB wins, else localStorage
  const effectiveOrder = dbOrder ?? localOrder;
  const hasCustomOrder = effectiveOrder !== null && effectiveOrder.length > 0;
  const isDbOrder = dbOrder !== null;
  const hasLocalOnly = dbOrder === null && (localOrder?.length ?? 0) > 0;

  const sortedRows = useMemo(() => {
    if (!effectiveOrder || effectiveOrder.length === 0) return defaultRows;
    return sortByCustomOrder(defaultRows, effectiveOrder, naturalSort);
  }, [defaultRows, effectiveOrder]);

  const invalidateTableQueries = useCallback(() => {
    if (!establishmentId) return;
    queryClient.invalidateQueries({ queryKey: tablesQueryKey(establishmentId) });
    queryClient.invalidateQueries({
      queryKey: combinedTablesQueryKey(establishmentId),
    });
  }, [queryClient, establishmentId]);

  const moveRow = useCallback(
    (index: number, direction: "up" | "down") => {
      if (!establishmentId) return;
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= sortedRows.length) return;

      const newRows = [...sortedRows];
      [newRows[index], newRows[targetIndex]] = [
        newRows[targetIndex],
        newRows[index],
      ];

      // Optimistic local update for instant feedback
      const newIds = newRows.map((r) => r.id);
      setLocalOrder(newIds);
      saveCustomTableOrder(establishmentId, newIds);

      // Persist to DB (shared across team/devices). Best-effort: on failure
      // the localStorage order still gives correct UX for the current user.
      persistOrderToDb(establishmentId, newRows)
        .then(() => invalidateTableQueries())
        .catch((err) => {
          console.warn("[table-order] DB persist failed:", err);
        });
    },
    [sortedRows, establishmentId, invalidateTableQueries]
  );

  const [isSyncing, setIsSyncing] = useState(false);

  const syncLocalToDb = useCallback(async () => {
    if (!establishmentId || !localOrder || localOrder.length === 0) return;
    setIsSyncing(true);
    try {
      // Reorder defaultRows according to localOrder
      const rowsInOrder = sortByCustomOrder(defaultRows, localOrder, naturalSort);
      await persistOrderToDb(establishmentId, rowsInOrder);
      invalidateTableQueries();
    } finally {
      setIsSyncing(false);
    }
  }, [establishmentId, localOrder, defaultRows, invalidateTableQueries]);

  const resetOrder = useCallback(async () => {
    if (!establishmentId) return;
    setLocalOrder(null);
    clearCustomTableOrder(establishmentId);
    try {
      await clearOrderInDb(establishmentId);
      invalidateTableQueries();
    } catch (err) {
      console.warn("[table-order] DB clear failed:", err);
    }
  }, [establishmentId, invalidateTableQueries]);

  return {
    sortedRows,
    hasCustomOrder,
    isDbOrder,
    hasLocalOnly,
    isSyncing,
    moveRow,
    resetOrder,
    syncLocalToDb,
  };
}
